import { createReducer, Action, on } from '@ngrx/store';
import { uniq } from 'lodash-es';

import { DealComparisonActions } from './deal-comparison.actions';
import { DealComparisonState, DEFAULT_LIMIT } from './deal-comparison.state';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { AppActions } from '../app/app.actions';
import { DealFilterActions } from '../deal-filter/deal-filter.actions';

export const initialState: DealComparisonState = {
  loading: false,
  isVisible: false,
  deals: [],
  basket: [],
  downloadEnabled: false,
  directUrlWarning: false,
  dealsToDownload: [],
  limit: DEFAULT_LIMIT,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      DealComparisonActions.SetCompareSuccess,
      (state, action): DealComparisonState => ({
        ...state,
        deals: action.deals,
        dealsToDownload: action.dealsToDownload,
      })
    ),
    on(
      DealComparisonActions.DownloadDeals,
      (state, action): DealComparisonState => ({
        ...state,
        error: undefined,
        loading: true,
      })
    ),
    on(
      DealComparisonActions.DownloadDealsFail,
      (state, action): DealComparisonState => ({
        ...state,
        loading: false,
        error: action.error,
      })
    ),
    on(
      DealComparisonActions.DownloadDealsSuccess,
      (state, action): DealComparisonState => ({
        ...state,
        error: undefined,
        dealsToDownload: [],
        deals: action.deals,
      })
    ),
    on(
      DealComparisonActions.SetVisibility,
      (state, action): DealComparisonState => ({
        ...state,
        isVisible: action.isVisible,
      })
    ),
    on(
      // The requested id lives only in the deal-filter store; this marks the fetch in flight.
      // `setDealFilterIdEffect$` bails on a falsy id, so arming the flag for one sticks for the session.
      DealComparisonActions.SetDealFilterId,
      (state, action): DealComparisonState => ({
        ...state,
        loading: !!action.dealFilterId,
        directUrlWarning: false,
      })
    ),
    on(
      // Choosing the Basket is choosing a scope, so it leaves a link that is showing.
      DealFilterActions.UseBasket,
      (state): DealComparisonState => ({
        ...state,
        directUrlWarning: false,
      }),
    ),
    on(
      DealComparisonActions.ClearComparison,
      (state): DealComparisonState => ({
        ...state,
        loading: false,
        deals: [],
        dealsToDownload: [],
        comparison: undefined,
        directUrlWarning: false,
      })
    ),
    on(
      DealComparisonActions.SetLimit,
      (state, action): DealComparisonState => ({
        ...state,
        limit: action.limit,
      })
    ),
    on(
      DealComparisonActions.CompareSuccess,
      (state, action): DealComparisonState => ({
        ...state,
        loading: false,
        comparison: action.comparison,
      })
    ),
    on(
      DealComparisonActions.SetDownloadEnabled,
      (state, action): DealComparisonState => ({
        ...state,
        downloadEnabled: action.downloadEnabled,
      })
    ),
    on(
      DealComparisonActions.AddToBasket,
      (state, action): DealComparisonState => ({
        ...state,
        basket: uniq([...state.basket, ...action.transactionIds]),
      })
    ),
    on(
      AppActions.LoadInitDataSuccess,
      (state, action): DealComparisonState => ({
        ...state,
        basket: uniq(action.data.assetSettings?.basket || []),
      })
    ),
    on(
      DealComparisonActions.SetBasket,
      DealComparisonActions.ReplaceBasket,
      (state, action): DealComparisonState => ({
        ...state,
        basket: uniq(action.basket || []),
      })
    ),
    on(
      DealComparisonActions.SetCompare,
      (state, action): DealComparisonState => ({
        ...state,
        directUrlWarning: action.directUrlWarning
          ? action.directUrlWarning
          : state.directUrlWarning,
      })
    ),
    on(
      DealComparisonActions.RemoveFromBasket,
      (state, action): DealComparisonState => ({
        ...state,
        basket: state.basket.filter((id) => id !== action.transactionId),
      })
    ),
    on(
      DealComparisonActions.ClearBasket,
      (state, action): DealComparisonState => ({
        ...state,
        basket: [],
      })
    )
  )
);

export function reducer(state: DealComparisonState | undefined, action: Action) {
  return featureReducer(state, action);
}
